import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const CRUMB_MAP = {
  '/':                 ['Dashboard'],
  '/students':         ['Students'],
  '/hosts':            ['Hosts'],
  '/doc-tracker':      ['Doc Tracker'],
  '/reports':          ['Reports'],
  '/email-templates':  ['Email Templates'],
  '/settings':         ['Settings'],
};

function fmtRelTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function NotificationBell() {
  const navigate = useNavigate();
  const dropRef = useRef(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifs(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 30000);
    return () => clearInterval(iv);
  }, [fetchNotifs]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open) fetchNotifs();
  };

  const handleClickNotif = async (n) => {
    setOpen(false);
    try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    // Reading it removes it from the bar
    setNotifs(prev => prev.filter(x => x.id !== n.id));
    if (!n.read) setUnread(prev => Math.max(0, prev - 1));
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try { await api.patch('/notifications/read-all'); } catch {}
    setNotifs([]);
    setUnread(0);
  };

  return (
    <div className="notif-wrap" ref={dropRef}>
      <button
        className="topbar-icon-btn notif-bell-btn"
        title="Notifications"
        onClick={handleOpen}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-drop-head">
            <span className="notif-drop-title">Notifications</span>
            {unread > 0 && (
              <button className="notif-mark-all-btn" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>
          <div className="notif-drop-list">
            {notifs.length === 0 ? (
              <div className="notif-drop-empty">No notifications yet.</div>
            ) : notifs.map(n => (
              <div
                key={n.id}
                className={`notif-drop-item${n.read ? '' : ' notif-drop-item--unread'}`}
                onClick={() => handleClickNotif(n)}
              >
                {!n.read && <span className="notif-unread-dot" />}
                <div className="notif-drop-content">
                  <div className="notif-drop-item-title">{n.title}</div>
                  <div className="notif-drop-item-body">{n.message}</div>
                  <div className="notif-drop-item-time">{fmtRelTime(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export default function Topbar({ onBurgerClick }) {
  const location = useLocation();
  const pathname = location.pathname;

  let crumbs = CRUMB_MAP[pathname];
  if (!crumbs) {
    if (pathname.startsWith('/students/')) crumbs = ['Students', 'Student Detail'];
    else if (pathname.startsWith('/hosts/'))   crumbs = ['Hosts', 'Host Detail'];
    else crumbs = [pathname.replace('/', '')];
  }

  return (
    <div className="topbar">
      <button className="topbar-burger" onClick={onBurgerClick} aria-label="Toggle navigation">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">›</span>}
            <span className={i === crumbs.length - 1 ? 'here' : ''}>{c}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="topbar-actions">
        <NotificationBell />
      </div>
    </div>
  );
}
